import { useTranslation } from "react-i18next";
import type { Run } from "../api";

/**
 * What a run came to, as a bar and as words.
 *
 * THE WORDS ARE NOT DECORATION. The four verdict colours were checked for
 * colour-vision separation and pass comfortably; what nobody had measured is
 * their contrast against the surface they sit on, and three of the four are
 * under 3:1 - Bright Yellow at 1.50, Teleste Grey at 1.71, Bright Green at
 * 2.78. On a run like run-bawu3, where 14 of 21 cases are skipped, most of the
 * bar is a band at 1.71:1. A bar that cannot be read is a decoration; the
 * counts beside it are the encoding, and the bar shows the proportions.
 *
 * The colours themselves stay: they are the brand's, they are what the history
 * chart and every report already use, and `src/palette.test.ts` measures the
 * separation. This is the relief the contrast check asks for, not a repaint.
 *
 * THE DENOMINATOR IS THE POINT. The list showed `failed` as a bare number, so
 * "5" sat in a column next to "1901 steps" - five of what? The verdict
 * breakdown was already in the same SQL row and simply not read.
 */
const SEGMENTS = [
  ["failed", "var(--chart-failed)"],
  ["xfailed", "var(--chart-xfailed)"],
  ["skipped", "var(--chart-skipped)"],
  ["passed", "var(--chart-passed)"],
] as const;

export function verdictCounts(r: Run) {
  return {
    failed: r.failed_scenarios ?? 0,
    xfailed: r.v_xfailed ?? 0,
    skipped: r.v_skipped ?? 0,
    passed: r.v_passed ?? 0,
  };
}

export default function VerdictBar({ run, width = 120 }: { run: Run; width?: number }) {
  const { t } = useTranslation();
  const c = verdictCounts(run);
  const total = c.failed + c.xfailed + c.skipped + c.passed;

  // A run still going, or one that recorded nothing at all. Neither has a
  // shape yet, and drawing an empty track for them would read as "all skipped".
  if (!run.finished_at) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-success-700 dark:text-success-400">
        <span className="h-2 w-2 animate-pulse rounded-full bg-success-500" />
        {t("verdict.running")}
      </span>
    );
  }
  if (total === 0) {
    return (
      <span className="text-sm text-slate-500">
        {run.step_count > 0 ? t("verdict.noVerdicts") : t("verdict.nothingRecorded")}
      </span>
    );
  }

  return (
    <div>
      <span
        className="flex h-2 gap-[2px] overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"
        style={{ width }}
        role="img"
        aria-label={t("verdict.barLabel", {
          failed: c.failed, passed: c.passed,
          skipped: c.skipped, xfailed: c.xfailed,
        })}
      >
        {SEGMENTS.map(([k, colour]) =>
          c[k] > 0 ? (
            <span
              key={k}
              style={{ background: colour, width: `${(100 * c[k]) / total}%` }}
              className="first:rounded-l-full last:rounded-r-full"
            />
          ) : null,
        )}
      </span>
      <div className="mt-0.5 text-xs tabular-nums text-slate-600 dark:text-slate-400">
        {c.failed > 0 ? (
          <span className="font-semibold text-red-600 dark:text-red-400">
            {t("verdict.failed", { count: c.failed })}
          </span>
        ) : (
          <span className="text-success-700 dark:text-success-400">
            {t("verdict.clean")}
          </span>
        )}
        {c.passed > 0 && <> · {t("verdict.passed", { count: c.passed })}</>}
        {/* "skipped" and "not applicable on this rig" are different facts and
            the corpus gates most cases on a capability, so on any one bench
            most of a suite skips BY DESIGN. Reading that as a coverage gap is
            the wrong conclusion, and it is the one the word invites. */}
        {c.skipped > 0 && <> · {t("verdict.naHere", { count: c.skipped })}</>}
        {c.xfailed > 0 && <> · {t("verdict.xfailed", { count: c.xfailed })}</>}
      </div>
    </div>
  );
}

